import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeftOutlined,
  EditOutlined,
  InfoCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Alert, Tooltip } from "antd";
import { useResponsive } from "../../hooks/useResponsive";
import { Link } from "react-router-dom";
import Spinner from "../Spinner";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import useUser from "../../hooks/useUser";
import { userState } from "../../store/slices/loginSlice";
import useGlobalHandler from "../../hooks/useGlobalHandler";
import { getUser, resetPassword } from "../../hooks/useSignup";
import store from "../../store/store";
import { useTranslation } from "react-i18next";

const SecuritySettings = () => {
  const { t } = useTranslation("postAuth", {
    keyPrefix: "profile",
  });

  const { mobileOnly } = useResponsive();
  const { isLoading, setIsLoading, setIsError, error, setError } =
    useGlobalHandler();
  const { updateUserProfile } = useUser();

  const [success, setSuccess] = useState();
  const [showPassword, setshowPassword] = useState(false);
  const user = store.getState().login.user;

  const handelSubmit = async (e) => {
    e.preventDefault();
    try {
      setError("");
      setIsLoading(true);
      await resetPassword(user.email);
      setIsLoading(false);
      setEdit(!edit);
      setSuccess("Email sent, check your inbox");
    } catch (error) {
      setError("faild to send email, try again");
    }
  };
  const [edit, setEdit] = useState(false);

  const updatedInfoSchema = Yup.object().shape({
    newEmail: Yup.string()
      .email("Invalid email")
      .required("New Email Required"),
    password: Yup.string()
      .required("No password provided.")
      .min(8, "Password is too short - should be 8 chars minimum.")
      .matches(/[a-zA-Z]/, "Password can only contain Latin letters."),
  });

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <Formik
        initialValues={{
          email: user?.email,
          newEmail: user?.email,
          password: "",
          uid: user?.uid,
        }}
        validationSchema={updatedInfoSchema}
        onSubmit={async (values) => {
          const { uid } = values;
          try {
            setIsError(false);
            setError("");
            setIsLoading(true);
            await updateUserProfile(values);
            const logedInUser = await getUser(uid);
            store.dispatch(userState({ ...logedInUser }));
            setIsLoading(false);
          } catch (error) {
            setIsLoading(false);
            setIsError(true);
            setError(error.message);
          }
        }}
      >
        {({ errors, touched, values, setFieldValue, field }) => (
          <div className="bg-white py-4 px-8 rounded-lg m-4 mediumShadow">
            {mobileOnly && (
              <span className="flex  justify-between">
                <motion.div whileTap={{ scale: 0.8 }} className="mb-8">
                  <Link to="/profile">
                    {" "}
                    <ArrowLeftOutlined />{" "}
                    <span className="font-semibold text-lg">Back</span>
                  </Link>
                </motion.div>
                <motion.div
                  whileTap={{ scale: 0.8 }}
                  className="mb-8 cursor-pointer"
                  onClick={() => setEdit(!edit)}
                >
                  Edit <EditOutlined />
                </motion.div>
              </span>
            )}
            <div className="flex flex-row justify-between items-center gap-4">
              <h1 className="LoginTtitle">
                Change Password{" "}
                <Tooltip
                  placement="topRight"
                  title={"  ارسال بريد إلكتروني لتغيير الرقم السري"}
                >
                  <InfoCircleOutlined />
                </Tooltip>{" "}
              </h1>
              {!mobileOnly && (
                <motion.div
                  whileTap={{ scale: 0.8 }}
                  className="cursor-pointer"
                  onClick={() => setEdit(!edit)}
                >
                  Edit <EditOutlined />
                </motion.div>
              )}
            </div>

            <Form className="flex flex-col gap-8  ">
              <div className="flex flex-col gap-1 mx-4">
                <motion.button
                  whileTap={{ scale: edit ? 0.9 : 1 }}
                  whileHover={{ scale: edit ? 1.05 : 1 }}
                  className=" primaryLoginButton w-auto self-center disabled:bg-primaryColor/50 cursor-pointer"
                  type="button"
                  onClick={handelSubmit}
                  disabled={!edit}
                >
                  Send Password Reset Email
                </motion.button>
              </div>
              <div className="flex flex-row justify-between items-center gap-4">
                <h1 className="LoginTtitle">
                  Change Email{" "}
                  <Tooltip
                    placement="topRight"
                    title={"   تغيير البريد الإلكتروني     "}
                  >
                    <InfoCircleOutlined />
                  </Tooltip>{" "}
                </h1>
              </div>

              <span className="w-full flex flex-col md:flex-row items-start md:items-center gap-2  whitespace-nowrap ">
                <label className="text-primaryColor">
                  {t("newEmail")} :{" "}
                  <Tooltip
                    placement="topRight"
                    title={" البريد الإلكتروني الجديد "}
                  >
                    <InfoCircleOutlined />
                  </Tooltip>{" "}
                </label>
                <Field
                  className={`border-2 rounded-md p-1 flex-1 w-full ${
                    user.emailVerified ? "border-lime-500" : "border-yellow-600"
                  }`}
                  name="newEmail"
                  placeholder={t("new email")}
                  disabled={!edit}
                  value={values.newEmail}
                />

                {user.emailVerified ? (
                  <Alert message="Verified" type="success" showIcon />
                ) : (
                  <motion.span
                    whileTap={{ scale: 0.9 }}
                    className="cursor-pointer"
                  >
                    <Alert message="Unverified" type="warning" showIcon />
                  </motion.span>
                )}
              </span>
              <span className="relative w-full flex flex-col md:flex-row items-start md:items-center gap-2  whitespace-nowrap ">
                <label className="text-primaryColor ">
                  {t("password")} :{" "}
                  <Tooltip placement="topRight" title={"  كلمة المرور    "}>
                    <InfoCircleOutlined />
                  </Tooltip>{" "}
                </label>
                <Field
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("password")}
                  className="border-2 rounded-md p-1 flex-1 w-full"
                  disabled={!edit}
                  value={values.password}
                />
                <EyeOutlined
                  className={`absolute top-[25%] right-2 ${
                    showPassword ? "" : "hidden"
                  }`}
                  onClick={() => setshowPassword(!showPassword)}
                />
                <EyeInvisibleOutlined
                  className={`absolute top-[25%] right-2 ${
                    !showPassword ? "" : "hidden"
                  }`}
                  onClick={() => setshowPassword(!showPassword)}
                />
              </span>
              <div className="flex flex-col gap-1 mx-4">
                <motion.button
                  whileTap={{ scale: edit ? 0.9 : 1 }}
                  whileHover={{ scale: edit ? 1.05 : 1 }}
                  className=" primaryLoginButton w-auto self-center disabled:bg-primaryColor/50 cursor-pointer"
                  type="submit"
                  disabled={!edit}
                >
                  Change Email
                </motion.button>
              </div>
            </Form>
            {success && <Alert message={success} type="success" />}
            {error && <Alert message={error} type="error" />}
          </div>
        )}
      </Formik>
    </>
  );
};

export default SecuritySettings;
